/**
 * PipelineStatusBar Component
 *
 * Compact status bar that appears at the top of all views when the pipeline is running.
 * Shows current stage, progress, and provides a quick link to the Pipeline view.
 */

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { GitBranch, X, ChevronRight, Loader2 } from "lucide-react";
import { usePipelineStore, PIPELINE_STAGES } from "../../stores/pipelineStore";

export default function PipelineStatusBar() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Pipeline state from store
  const {
    pipelineStatus,
    currentStage,
    stages,
  } = usePipelineStore();

  // Reset dismissed state when pipeline starts running
  useEffect(() => {
    if (pipelineStatus === "running") {
      setDismissed(false);
    }
  }, [pipelineStatus]);

  // Don't show if we're already on the pipeline page
  const isOnPipelinePage = location.pathname === "/pipeline" || location.pathname === "/";

  // Don't show if dismissed or not running (unless recently completed/failed)
  const isRunning = pipelineStatus === "running";
  const isCompleted = pipelineStatus === "completed";
  const isFailed = pipelineStatus === "failed";
  const showBar = !dismissed && (isRunning || isCompleted || isFailed) && !isOnPipelinePage;

  if (!showBar) {
    return null;
  }

  // Get current stage info
  const currentStageInfo = currentStage
    ? PIPELINE_STAGES.find((s) => s.id === currentStage)
    : null;

  // Get current stage progress
  const currentStageProgress = stages.find((s) => s.stage === currentStage);
  const progress = currentStageProgress?.progress?.percent ?? 0;
  const progressMessage = currentStageProgress?.progress?.message ?? "";

  // Determine status color
  const statusColors = {
    running: "bg-blue-500",
    completed: "bg-green-500",
    failed: "bg-red-500",
    pending: "bg-gray-500",
  };

  const bgColors = {
    running: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
    completed: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
    failed: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
    pending: "bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800",
  };

  const textColors = {
    running: "text-blue-700 dark:text-blue-300",
    completed: "text-green-700 dark:text-green-300",
    failed: "text-red-700 dark:text-red-300",
    pending: "text-gray-700 dark:text-gray-300",
  };

  return (
    <div
      className={`flex items-center justify-between px-4 py-2 border-b ${bgColors[pipelineStatus]}`}
    >
      <div className="flex items-center space-x-3">
        {/* Status indicator */}
        <div className="flex items-center space-x-2">
          {isRunning ? (
            <Loader2 className={`w-4 h-4 ${textColors[pipelineStatus]} animate-spin`} />
          ) : (
            <div className={`w-2 h-2 rounded-full ${statusColors[pipelineStatus]}`} />
          )}
          <span className={`text-sm font-medium ${textColors[pipelineStatus]}`}>
            {isRunning && currentStageInfo
              ? `Running: ${currentStageInfo.name}`
              : isCompleted
              ? "Pipeline Completed"
              : isFailed
              ? "Pipeline Failed"
              : "Pipeline"}
          </span>
        </div>

        {/* Progress bar (only when running) */}
        {isRunning && (
          <div className="flex items-center space-x-2">
            <div className="w-32 h-1.5 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[3rem]">
              {progress}%
            </span>
            {progressMessage && (
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                {progressMessage}
              </span>
            )}
          </div>
        )}

      </div>

      <div className="flex items-center space-x-2">
        {/* View Pipeline link */}
        <Link
          to="/pipeline"
          className={`flex items-center space-x-1 text-sm ${textColors[pipelineStatus]} hover:underline`}
        >
          <GitBranch className="w-4 h-4" />
          <span>View Pipeline</span>
          <ChevronRight className="w-4 h-4" />
        </Link>

        {/* Dismiss button (only when not running) */}
        {!isRunning && (
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
}
